import { useState, useEffect, useRef } from 'react';
import { SimulationEngine } from '../../utils/simulator-v3/SimulationEngine.js';
import MicroView from './MicroView.jsx';
import MacroView from './MacroView.jsx';

/**
 * LifeSimulation - Main container for "Lives in Motion"
 * 
 * Manages:
 * - Simulation engine lifecycle
 * - View switching (micro/macro)
 * - Global state updates
 */
export default function LifeSimulation() {
  const [simulationState, setSimulationState] = useState(null);
  const [viewMode, setViewMode] = useState('micro'); // 'micro' or 'macro'
  const [focusedLifeId, setFocusedLifeId] = useState(0);
  
  const engineRef = useRef(null);
  const updateIntervalRef = useRef(null);

  // Initialize simulation engine
  useEffect(() => {
    const engine = new SimulationEngine({
      startWithLives: 1,
      maxLives: 10,
      enableMacroEffects: false
    });
    
    engineRef.current = engine;
    engine.start();
    
    // Update React state at 30 FPS (simulation runs at 60 FPS internally)
    updateIntervalRef.current = setInterval(() => {
      if (engine) {
        setSimulationState(engine.getSystemState());
      }
    }, 1000 / 30);
    
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      if (engine) {
        engine.cleanup();
      }
    };
  }, []);

  // Control functions
  const togglePause = () => {
    if (!engineRef.current) return;
    
    if (simulationState?.isPaused) {
      engineRef.current.resume();
    } else {
      engineRef.current.pause();
    }
  };

  const setTimeScale = (scale) => {
    if (!engineRef.current) return;
    engineRef.current.setTimeScale(scale);
  };

  const setBehavior = (zone, parameter, value) => {
    if (!engineRef.current) return;
    engineRef.current.setBehavior(focusedLifeId, zone, parameter, value);
  };

  const setCoverage = (zone, level) => {
    if (!engineRef.current) return;
    engineRef.current.setCoverage(focusedLifeId, zone, level);
  };

  const setLifestyle = (parameter, value) => {
    if (!engineRef.current) return;
    engineRef.current.setLifestyle(focusedLifeId, parameter, value);
  };

  const injectEvent = (eventType) => {
    if (!engineRef.current) return;
    engineRef.current.injectEvent(eventType, focusedLifeId);
  };

  const zoomOut = () => {
    if (!engineRef.current) return;
    
    // Expand to macro (add more lives)
    engineRef.current.expandToMacro(9);
    setViewMode('macro');
  };

  const zoomIn = (lifeId) => {
    setFocusedLifeId(lifeId);
    setViewMode('micro');
  };

  if (!simulationState) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0a0f] text-white">
        <div className="text-center">
          <div className="text-2xl font-mono mb-4">Initializing...</div>
          <div className="w-48 h-1 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header - Minimal */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-mono tracking-wide">Lives in Motion</h1>
            {viewMode === 'micro' && (
              <span className="text-sm text-gray-400 font-mono">
                {simulationState.lives.length > 1 ? `Life ${focusedLifeId + 1} of ${simulationState.lives.length}` : 'One Life'}
              </span>
            )}
          </div>
          
          {/* Time controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={togglePause}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors font-mono text-sm"
            >
              {simulationState.isPaused ? 'Resume' : 'Pause'}
            </button>
            
            <select
              value={simulationState.timeScale}
              onChange={(e) => setTimeScale(parseFloat(e.target.value))}
              className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors font-mono text-sm border border-white/10"
            >
              <option value="0.5">0.5x</option>
              <option value="1.0">1.0x</option>
              <option value="2.0">2.0x</option>
              <option value="3.0">3.0x</option>
            </select>
            
            {viewMode === 'micro' && simulationState.lives.length === 1 && (
              <button
                onClick={zoomOut}
                className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-colors font-mono text-sm border border-blue-500/30"
              >
                Zoom Out
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20">
        {viewMode === 'micro' ? (
          <MicroView
            life={simulationState.lives[focusedLifeId]}
            queuedEvents={simulationState.queuedEvents.filter(e => e.targetLife?.id === focusedLifeId)}
            onBehaviorChange={setBehavior}
            onCoverageChange={setCoverage}
            onLifestyleChange={setLifestyle}
            onInjectEvent={injectEvent}
            isPaused={simulationState.isPaused}
          />
        ) : (
          <MacroView
            lives={simulationState.lives}
            macroState={simulationState.macroState}
            onZoomIn={zoomIn}
            queuedEvents={simulationState.queuedEvents}
          />
        )}
      </div>

      {/* Footer - Ambient info */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-sm border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between text-xs text-gray-500 font-mono">
          <div>Time: {Math.floor(simulationState.currentTime)}s</div>
          {viewMode === 'macro' && (
            <div>System Strain: {(simulationState.macroState.systemStrain * 100).toFixed(0)}%</div>
          )}
          <div>v0.3 - Lives in Motion</div>
        </div>
      </div>
    </div>
  );
}

